import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { css } from '@emotion/css';
import PropTypes from 'prop-types';
import AppLabels from '../../utils/appLabels';
import UserMessage from '../../common/UserMessage/UserMessage';

const generalStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 20rem;
  margin-right: 1rem;
  margin-bottom: 1rem;
  border-radius: 0.625rem;
  border: 0.0625rem solid #ddd9d5;
  background: #fff;
  padding: 1.125rem;

  @media (max-width: 641px) {
    min-width: initial;
    width: 100%;
    box-sizing: border-box;
  }
`;

const addressTypeStyle = css`
  font-size: 1rem;
  font-style: normal;
  font-weight: 600;
  line-height: 1.125rem;
  margin-bottom: 0.75rem;
`;

const addressDetailsStyle = css`
  font-size: 1rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1.5rem;
`;

const submitButtonStyle = css`
  margin: 1.5rem 2rem 0;
  cursor: pointer;
  font-size: 14px;
  line-height: 21px;
  border-radius: 100px;
  border: 1px solid #7b1a27;
  padding: 0.5rem 1rem;
`;

const unselectedButtonStyle = css`
  background: #fff;
  color: #7b1a27;
  border: 0.0625rem solid #7b1a27;
`;

const selectedButtonStyle = css`
  background: #f1f0ee;
  color: #535250;
  cursor: initial;
  border: none;
`;

const highlightedStyle = css`
  color: #fff;
  background-color: #7b1a27;
`;

const updateButtonContainerStyle = css`
  margin-top: 0.5rem;
  background: 0;
  border: 0;
  text-align: left;
  padding: 0;
  font-size: 1rem;
`;

const updateButtonStyle = css`
  color: #7b1a27;
  text-decoration: underline;
  margin-top: 0.5rem;
  cursor: pointer;
  font-weight: 600;
`;

// ── Delete-address confirmation modal (fully standalone, ADA-compliant) ──────

const deleteOverlayStyle = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(83, 82, 80, 0.5);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  box-sizing: border-box;
`;

const deleteBoxStyle = css`
  background: #fff;
  border-radius: 1rem;
  padding: 1.5rem 1.5rem 1.25rem;
  max-width: 24rem;
  width: 100%;
  box-shadow: 0 0 2rem 0 rgba(56, 51, 46, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  box-sizing: border-box;
`;

const deleteCloseButtonStyle = css`
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;

const deleteMessageStyle = css`
  text-align: center;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  margin: 0.5rem 0 1.25rem;
  padding: 0 1rem;
`;

const deleteActionsStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
`;

const deleteConfirmButtonStyle = css`
  cursor: pointer;
  font-size: 0.875rem;
  line-height: 1.3125rem;
  border-radius: 100px;
  padding: 0.5rem 1rem;
  background: #7b1a27;
  color: #fff;
  border: none;
  min-width: 10rem;
  width: 100%;
  max-width: 16rem;
`;

const deleteCancelButtonStyle = css`
  color: #7b1a27;
  text-decoration: underline;
  font-weight: 600;
  font-size: 1rem;
  background: none;
  border: none;
  cursor: pointer;
`;

const srOnlyStyle = css`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
`;

type Purpose = { name: string };

const getDeleteInfoMessage = (purposes: Purpose[]): string | null => {
  const names = (purposes || []).map((p) => p.name);
  if (names.includes('AR_DELIVERY')) {
    return 'This address is used for your subscription delivery order. If you remove it here, it may still appear because it\'s used for your ecommerce orders.';
  }
  if (names.length > 0) {
    return 'This address is used for your ecom orders. If you delete it you will have to enter it again when placing a new order.';
  }
  return null;
};

const DeleteAddressConfirmModal = ({
  show,
  onClose,
  onConfirm,
  purposes = [],
}: {
  show: boolean;
  onClose: () => void;
  onConfirm: () => void;
  purposes?: Purpose[];
}) => {
  const removeRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!show) return;
    // Set focus on the primary action when modal opens
    removeRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const focusable = [removeRef.current, cancelRef.current].filter(Boolean) as HTMLButtonElement[];
        const activeIndex = focusable.indexOf(document.activeElement as HTMLButtonElement);
        if (e.shiftKey) {
          if (activeIndex <= 0) {
            e.preventDefault();
            focusable[focusable.length - 1]?.focus();
          }
        } else {
          if (activeIndex === focusable.length - 1) {
            e.preventDefault();
            focusable[0]?.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [show, onClose]);

  if (!show) return null;

  const infoMessage = getDeleteInfoMessage(purposes);
  const portal = document.getElementById('root') || document.body;

  return ReactDOM.createPortal(
    <div
      id="delete-address-confirm-modal-overlay"
      className={deleteOverlayStyle}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        id="delete-address-confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-addr-title"
        aria-describedby="delete-addr-desc"
        className={deleteBoxStyle}
      >
        {/* Visually hidden title for screen readers */}
        <h2 id="delete-addr-title" className={srOnlyStyle}>Remove Address</h2>

        <button
          className={deleteCloseButtonStyle}
          onClick={onClose}
          aria-label="Close remove address dialog"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M12 4L4 12M4 4l8 8" stroke="#535250" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        <p id="delete-addr-desc" className={deleteMessageStyle}>
          Are you sure you want to remove this address?
        </p>

        {infoMessage && (
          <UserMessage
            messageType="warning"
            messageText={infoMessage}
            className={css`margin-bottom: 1rem; width: 100%;`}
          />
        )}

        <div className={deleteActionsStyle}>
          <button
            ref={removeRef}
            className={deleteConfirmButtonStyle}
            onClick={onConfirm}
            aria-label="Confirm remove address"
          >
            Remove
          </button>
          <button
            ref={cancelRef}
            className={deleteCancelButtonStyle}
            onClick={onClose}
            aria-label="Cancel remove address"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    portal,
  );
};

/**
 * selected means the currently selected address from the backend
 * highlighted means it's just locally selected by the user, before saving any changes
 * @param address
 * @param selected
 * @param highlighted
 * @param onUseAddress
 * @param onUpdateAddress
 * @constructor
 */
const DeliveryAddressTile = ({ address, selected, highlighted, onUseAddress, onUpdateAddress, onDeleteAddress }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const addressDetails = `${address.city}, ${address.state} ${address.zipCode}`;
  const fullAddress1 = `${address.address1}${address.address2 ? ' ' + address.address2 : ''}`;
  return (
    <div className={generalStyle}>
      <DeleteAddressConfirmModal
        show={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => { setShowDeleteConfirm(false); onDeleteAddress(address); }}
        purposes={address.purposes || []}
      />
      <div className={addressTypeStyle}>{address.addressType.description}</div>
      <div className={addressDetailsStyle}>{fullAddress1}</div>
      <div className={addressDetailsStyle}>{addressDetails}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <button className={updateButtonContainerStyle} onClick={() => onUpdateAddress(address)}>
          <span role="button" className={updateButtonStyle}>
            {AppLabels.DeliveryTabForm.UpdateAddressButton}
          </span>
        </button>
        {!selected && (
          <>
            <span style={{alignSelf: "self-end"}}>|</span>
            <button className={updateButtonContainerStyle} onClick={() => setShowDeleteConfirm(true)}>
              <span role="button" className={updateButtonStyle}>
                {AppLabels.DeliveryTabForm.DeleteAddressButton}
              </span>
            </button>
          </>
        )}
      </div>
      <input
        tabIndex={0}
        aria-label={
          selected
            ? `Selected address ${address.address1}, ${addressDetails}`
            : `Use address ${address.address1}, ${addressDetails}`
        }
        aria-roledescription="button"
        type="submit"
        value={selected || highlighted ? 'Selected' : 'Use address'}
        disabled={selected}
        className={`${submitButtonStyle} ${selected ? selectedButtonStyle : highlighted ? highlightedStyle : unselectedButtonStyle}`}
        onClick={() => onUseAddress(address)}
      />
    </div>
  );
};

DeliveryAddressTile.propTypes = {
  address: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  highlighted: PropTypes.bool,
  onUseAddress: PropTypes.func,
  onUpdateAddress: PropTypes.func,
  onDeleteAddress: PropTypes.func,
};

export default DeliveryAddressTile;
